import BN from "bn.js";
import { Web3PromiEvent } from "postchain-client";
import { TransactionWithReceipt } from "@chromia/ft4";

import { iChromiaState } from "./chrInterfaces";
import { cBigNumber } from "../classes/BigNumber";
import { ReactNode } from "react";
import { cStrike } from "../classes/strike";
import { blockchain } from "../settings";

//  REDUX

export interface iReduxState {
	app: iAppState,
	chromia: iChromiaState,
	pro: iProState,
	basic: iBasicState,
	modals: iModalsState,
	UI: iUIState,
}

export interface iAppState {
	userAddress: string | null,
	config: iConfig | null,
	api: string | null,
	EOPTContract: iEOPTContract | null,
	tokens: tTokenInfo[],
	systemTimestamp: number | null,
	// web3Provider: iWeb3Provider | null,
	provider: iWeb3Provider | null
	isPro: boolean,
	tokensPrices: tTokenPrice[],
	currentOptions: iOption[],
	clientBalances: iBalance[],
	// clientOrders: iOrder[],
	clientOrders: iClientOrderOption[],
	darkMode: boolean,
	clientHistory: iClientHistory,
	allOptions: iOption[],     // just for history
	optionsPeriod: 'all' | 'weekly' | 'daily' | 'non-standart',
	KasAccessToken: string | null,
    chrNodeUrl: string[] | null;
}

export interface iProState {
	currentFToken: string | null,
	currentUToken: string | null,
	currentMaturityDate: tMaturityDate | null,
	optionsPeriod: 'weekly' | 'daily',
}

export interface iBasicState {
	currentStep: 0 | 1 | 2 | 3 | 4,
	currentFToken: string | null,
	currentUToken: string | null,
	currentDirection: 'up' | 'down' | null,
	currentMaturityDate: tMaturityDate | null,
	currentStrike: cStrike | null,
}

export interface iModalsState {
	modalsIds: {[K in tModalName]: number},
	openedModals: (iNonIdModal | iExactIdModal | iSimpleModal | iApproveModal | iMakeOrderModal | iTakeOrderModal | iRequestUncreateBorrowModal | iOrderBookModal | iInfoModal | iConfirmModal | iSuccessModal | iTransactionModal | iFinalizeExerciseModal)[],
}

export interface iUIState {
	isInited: boolean,
	isChrRegistered: boolean,
	isOrderBookModalButtonsBlocked: boolean,
	isWrongNetwork: boolean,
	isPricesUpdating: boolean,
	provider: any,
}
// /REDUX

export type tTokenInfo = {
	currency: string,
	address: string,
	// id?: Buffer,
	decimals: number,
	showDecimals: number,
	contract: iTokenContract,
	type: 'funding' | 'underlying' | 'commission',
}

type tTokenPrice = {
	currency: string,
	price: cBigNumber,
}

// export interface iWeb3Provider {   // different objects for different wallets
// 	isMetaMask?: boolean,
// 	disconnect?: () => void,
// 	on?: (event: string, callback: Function) => Function,
// 	once?: (event: string, callback: Function) => Function,
// 	off: (event: string, callback?: Function) => void,
// 	clearCachedProvider: () => void,
// }

export interface iWeb3Provider {  
    isMetaMask?: boolean,
    disconnect?: 
        () => void,
    on?: (
        event: string, 
        callback: Function
    ) => Function,
    once?: ( 
        event: string, 
        callback: Function
    ) => Function,
    removeListener?: (
        event: string, 
        callback: Function
    ) => Function
}

export interface iConfigResponse {
	eopt: iConfigEoptResponse,
	tokens: iConfigTokensResponse,
	strikes: iConfigStrikesResponse,
}

export interface iConfigEoptResponse {
	abi: string,
	adr: string,
	autoexercise_time: number,
}

export interface iConfigTokensResponse {
	abi: string,
	f_tokens: iConfigTokenResponse[];
	u_tokens: iConfigTokenResponse[];
}

export interface iConfigTokenResponse{
	adr: string,
	name: string,
	dec: number,
	show_dec: number,
}

export interface iConfigStrikesResponse {
	dates: iConfigStrikeDateResponse[]
}

export interface iConfigStrikeDateResponse {
	/**
	 * Format: "YYYY-MM-DD"
	 */
	date: string,
	/**
	 * Timestamp for "YYYY-MM-DDT08:00Z" - standart opt expiration date
	 */
	ts: number,
	f_tokens: iConfigFTokenResponse[]
}

export interface iConfigFTokenResponse {
	currency: string,
	u_tokens: iConfigUTokenResponse[]
}

export interface iConfigUTokenResponse {
	currency: string,
	strikes: string[]   //  '560' / '5600' / '56000' etc.
}

export interface iConfig {
	eopt: iConfigEopt,
	tokens: iConfigTokens,
	strikes: iConfigStrikes,
}

export interface iConfigEopt {
	abi: string,
	address: string,
	autoexerciseTime: number,
}

export interface iConfigTokens {
	abi: string,
	fTokens: iConfigToken[];
	uTokens: iConfigToken[];
}

export interface iConfigToken{
	address: string,
	name: string,
	decimals: number,
	showDecimals: number,
}

export interface iConfigStrikes {
	dates: iConfigStrikeDate[]
}

export interface iConfigStrikeDate {
	/**
	 * Format: "YYYY-MM-DD"
	 */
	date: string,
	/**
	 * Timestamp for "YYYY-MM-DDT08:00Z" - standart opt expiration date
	 */
	ts: number,
	fTokens: iConfigFToken[]
}

export interface iConfigFToken {
	currency: string,
	uTokens: iConfigUToken[]
}

export interface iConfigUToken {
	currency: string,
	strikes: cStrike[]
}

export interface iEOPTContract {
	_address: string,
	methods: {
		GetVersion: () => { call: () => Promise<string> },
		now_time: () => { call: () => Promise<string> },
		set_time: (ts: number) => iSendMethod,
		UncreateOpt: (optId: number | string, amount: BN) => iSendMethod,
		WithdrawOpt: (userAddress: string, optId: number | string) => iSendMethod,     // can be done to anyone
		RequestOptExercise: (optId: number | string, amount: BN) => iSendMethod,
		FinalizeOptExercise: (userAddress: string, optId: number | string) => iSendMethod,  // can be done to anyone
		CreateOpt: (
			fTokenAddress: string,
			uTokenAddress: string,
			optionType: boolean,
			strikePrice: BN,
			maturityTime: number,
			optionAmount: BN,
		) => iSendMethod,
		TakeSellOrd: (
			optId: number | string,
			ordId: number | string,
			amount: BN,
			curPrice: BN,      // signer price
			priceTime: number,     // signer time
			v: string,
			r: string,
			s: string,
		) => iSendMethod,
		MakeSellOrd: (
			optId: number | string,
			amount: BN,
			refPrice: BN,
			delta: BN,
			ordPrice: BN,
			minOrderPrice: BN,
		) => iSendMethod,
		ChangeSellOrd: (
			optId: number | string,	// number
			ordId: number | string,	// number
			amount: BN | 0,
			refPrice: BN | 0,
			delta: BN | 0,
			ordPrice: BN | 0,
			minOrdPrice: BN | 0,
		) => iSendMethod,
		TakeBuyOrd: (
			optId: number | string,
			ordId: number | string,
			amount: BN,
			curPrice: BN,      // signer price
			priceTime: number,     // signer time
			v: string,
			r: string,
			s: string,
		) => iSendMethod,
		MakeBuyOrd: (
			optId: number | string,
			amount: BN,
			refPrice: BN,
			delta: BN,
			ordPrice: BN,
			maxOrderPrice: BN,
		) => iSendMethod,
		ChangeBuyOrd: (
			optId: number | string,	// number
			ordId: number | string,	// number
			amount: BN | 0,
			refPrice: BN | 0,
			delta: BN | 0,
			ordPrice: BN | 0,
			maxOrdPrice: BN | 0,
		) => iSendMethod,
		MatchOrds: (
			optId: number,
			sellOrdId: number,
			buyOrdId: number,
			curPrice: BN,      // signer price
			priceTime: number,     // signer ts
			v: string,
			r: string,
			s: string,
		) => iSendMethod,
		Borrow: (
			optId: number | string,
			amount: BN
		) => iSendMethod,
		SetBorrower: (
			userAddress: string,
			value: boolean
		) => iSendMethod,
		AcceptTsOptReq: (
			optId: number,
			requestId: number,
			price: BN,      // from sign
			ts: number,     // from sign
			v: string,
			r: string,
			s: string,
		) => iSendMethod,
	}
}

export interface iTokenContract {
	_address: string,
	methods: {
		allowance: (client_addr: string, rfq_contract_addr: string) => { call: () => Promise<string> },
		balanceOf: (client_addr: string) => { call: () => Promise<string> },
		approve: (rfq_contract_addr: string, amount: BN) => iSendMethod,
	}
}

interface iSendMethod {
	send: (options: {
		from: string,
		gas?: number,
		gasPrice?: string,
	}) => Promise<any>
}

export type tModalName =
	'selectOption' |
	'mobileMenu' |
	'chrBalancesModal' |
	'createOption' |
	'history' |
	'approve' |
	'makeOrder' |
	'takeOrder' |
	'requestUncreateBorrow' |
	'orderBook' |
	'info' |
	'wallet' |
	'confirmation' |
	'success' |
	'transaction' |
	'finalizeExercise'
;

export interface iModal {
	modalName: tModalName,
	modalId: number,
}

export interface iNonIdModal {
	// modalName: Extract<tModalName, 'mobileMenu' | 'chrBalancesModal'>,
	// modalName: Extract<tModalName, 'chrBalancesModal'>,
	modalName: Extract<tModalName, 'mobileMenu'>,
	modalId: undefined,
}

export interface iExactIdModal extends iModal {
	modalName: Extract<tModalName, 'selectOption'>,
}

export interface iSimpleModal extends iModal {
	// modalName: Extract<tModalName, 'createOption' | 'history'>,
	modalName: Extract<tModalName, 'createOption' | 'history' | 'wallet'>,
}

export interface iApproveModal extends iModal {
	modalName: Extract<tModalName, 'approve'>,
	approveToken: string | undefined,
	approveAmount: cBigNumber | undefined,
	approvePromise: iPromiseOptions | undefined,
	isOptionBlocked: boolean,
}

export interface iMakeOrderModal extends iModal {
	modalName: Extract<tModalName, 'makeOrder'>,
	type: 'Create' | 'Modify' | null,
	optId: number | string | null,
	order: {id: number | string, isSell: boolean} | null,
}
/* export type tMakeOrderModal = iEmptyMakeOrderModal | iCreateMakeOrderModal | iModifyMakeOrderModal;
interface iEmptyMakeOrderModal extends iModal {
	modalName: Extract<tModalName, 'makeOrder'>,
	type: null,
	optId: null,
	order: null,
}
interface iCreateMakeOrderModal extends iModal {
	modalName: Extract<tModalName, 'makeOrder'>,
	type: 'Create',
	optId: number | string,
	order: null,
}
interface iModifyMakeOrderModal extends iModal {
	modalName: Extract<tModalName, 'makeOrder'>,
	type: 'Modify',
	optId: number | string,
	order: {id: number | string, is_sell: boolean},
} */

export interface iTakeOrderModal extends iModal {
	modalName: Extract<tModalName, 'takeOrder'>,
	optId: number | string | null,
	isSell: boolean | null,
}

export interface iRequestUncreateBorrowModal extends iModal {
	modalName: Extract<tModalName, 'requestUncreateBorrow'>,
	type: 'request' | 'uncreate' | 'borrow' | null;
	balanceOptId: number | string | null;
}

export interface iOrderBookModal extends iModal {
	modalName: Extract<tModalName, 'orderBook'>,
	optionId: number | string | null,
	// isButtonsBlocked: boolean,
}

export interface iInfoModal extends iModal {
	modalName: Extract<tModalName, 'info'>,
	/**
	 * For new line in string use `\n`.
	 * 
	 * For new line in ReactNode use `<br/>`.
	 */
	message: ReactNode | null,
}

export interface iConfirmModal extends iModal {
	modalName: Extract<tModalName, 'confirmation'>,
	promise: iPromiseOptions,
	option: iOption,
}

export interface iSuccessModal extends iModal {
	modalName: Extract<tModalName, 'success'>,
	tx: string,
}

export interface iTransactionModal extends iModal {
	modalName: Extract<tModalName, 'transaction'>,
	transactionParams:
		// {blockchain: Extract<typeof blockchain, 'binance'>, transaction: iPromiEvent<iTransactionReceipt>} |
		{blockchain: Extract<typeof blockchain, 'chromia'>, transaction: Web3PromiEvent<TransactionWithReceipt, {built: Buffer;sent: Buffer;}>}
}

export interface iFinalizeExerciseModal extends iModal {
	modalName: Extract<tModalName, 'finalizeExercise'>,
	option: iOption,
	promise: iPromiseOptions,
	/**
	 * Amount + token currency.
	 */
	payout: string,
}

export type tModalsUnion =
	iExactIdModal |
	Omit<iNonIdModal, 'modalId'> |
	Omit<iSimpleModal, 'modalId'> |
	Omit<iApproveModal, 'modalId'> |
	Omit<iMakeOrderModal, 'modalId'> |
	Omit<iTakeOrderModal, 'modalId'> |
	Omit<iRequestUncreateBorrowModal, 'modalId'> |
	Omit<iOrderBookModal, 'modalId'> |
	Omit<iInfoModal, 'modalId'> |
	Omit<iConfirmModal, 'modalId'> |
	Omit<iSuccessModal, 'modalId'> |
	Omit<iTransactionModal, 'modalId'> |
	Omit<iFinalizeExerciseModal, 'modalId'>
;

export interface iPriceResponse {
	timestamp: number,
	price: number,
	sign: null,
}

export interface iSignedPriceResponse {
	timestamp: number,
	price: string,  // with big decimal
	sign: tSigner
}

export interface iSignedPrice {
	timestamp: number,
	price: cBigNumber,
	sign: tSigner
}

type tSigner = {
	v: string,
	r: string,
	s: string,
}

export interface iAllPricesPrice {
	name: string,
	timestamp: number,
	price: number | null,
}

export interface iOptionResponse {
	opt_id: number | string,
	f_token: string,    // address
	u_token: string,    // address
	call_type: boolean,    // true => call, false => put
	// strike: number,
	strike: string,
	create_time: number,
	maturity_time: number,
	// maturity_price: number,
	maturity_price: string,
	orders?: iOrderResponse[],
}

export interface iOptionResponseOrderless {
	// opt_id: number,
	opt_id: number | string,
	f_token: string,    // address
	u_token: string,    // address
	call_type: boolean,    // true => call, false => put
	strike: string,
	create_time: number,
	maturity_time: number,
	maturity_price: string,
}

export interface iOption {
	optId: number | string,
	fTokenCurrency: string,
	fTokenAddress: string,
	uTokenCurrency: string,
	uTokenAddress: string,
	/**
	 * True - call,
	 * 
	 * False - put
	 */
	optionType: boolean,
	optionTypeText: 'Put' | 'Call',
	strike: cStrike,
	// create_time: number,
	maturityTime: number,
	period: 'daily' | 'weekly',
	maturityPrice: cBigNumber,
	ordersBuy: iOrder[],
	ordersSell: iOrder[],
}

export interface iOrderResponse {
	ord_id: number,
	ord_type: boolean,  // true => sell, false => buy
	opt_id: number,
	owner: string,
	amount: string,
	ref_price: string,
	delta: string,
	ord_price: string,
	min_max_price: string,
	option?: iOptionResponse,
}

export interface iOrder {
	ordId: number | string,     // in chromia in client_balances - number, in current_options - string (example: '_OPT BTC-USDT 34000 1649404800 call a_C')
	ordType: boolean,  // true => sell, false => buy
	optId: number | string,
	owner: string,
	amount: cBigNumber,
	refPrice: cBigNumber,
	delta: cBigNumber,
	ordPrice: cBigNumber,
	minMaxPrice: cBigNumber,
	currentPrice: cBigNumber,
	// option?: iOption,
}

export interface iBalanceResponse {
	full_balance: string,
	locked_balance: string,
	exercise_balance: string,
	created_balance: string,
	borrow_balance: string,
	opt: iOptionResponseOrderless,

	// opt_id: number | undefined,     // only for chromia!!! missing in bsc response
	opt_id: Buffer | undefined,     // only for chromia!!! missing in bsc response
}

export interface iBalance {
	availableBalance: cBigNumber,
	fullBalance: cBigNumber,
	lockedBalance: cBigNumber,
	exerciseBalance: cBigNumber,
	createdBalance: cBigNumber,
	borrowBalance: cBigNumber,
	isShowedYourDesc: boolean,
	isShowedCreatedDesc: boolean,
	fullExButtonShowed: boolean,
	cancelExButtonShowed: boolean,
	finalizeExButtonShowed: boolean,
	uncreateButtonShowed: boolean,
	withdrawButtonShowed: boolean,
	borrowButtonShowed: boolean,
	opt: iOption,
	isExpired: boolean,

	optId: string | undefined,     // only for chromia!!! missing in bsc. opt_id !== opt.opt_id
	isShowedChrDesc: boolean,
}

export interface iExpiredOption {
	isFinalizeAvailable: boolean,
	finalized: boolean,
	opt: iOption,
}

export interface iTableOrder extends iOrder {
	isExpired: boolean
}
export interface iClientOrderOption extends Omit<iOption, 'ordersBuy' | 'ordersSell'> {
	order: iTableOrder
}

export type tMaturityDate = {
	ts: number,
	date: string,
}

export interface iPromiseOptions {
	resolve: null | ( (value: unknown) => void ),
	reject: null | ( (value: unknown) => void )
}

interface iClientHistory {
	isUpdating: boolean,
	recordings: iHistoryRecording[]
}

export interface iHistoryResponseRecordings {
	blockNumber: number,
	logIdx: number,
	id: number,
	name: tHistoryRecordingNames,
	blockHash: string,
	time: number,   // ts
	txHash: string,
	data: string,
}

export interface iHistoryRecording extends iHistoryResponseRecordings {
	humanTime: string,     // D MMM YY, HH:mm
	action: string,
	details: string[],
}

export type tHistoryRecordingNames = 'CreateOption' | 'TakeBuyOrder' | 'TakeSellOrder' | 'MakeSellOrder' | 'FinalizeOptionExercise' | 'WithdrawOption' | 'RequestOptionExercise' | 'AcceptTsReq' | 'MakeBuyOrder' | 'NewTsReq' | 'ChangeBuyOrder' | 'ChangeSellOrder' | 'PushTsReq' | 'UncreateOption' | 'ChangeBorrow' | '';

export interface iTimesetRequestResponse {
	req_id: number,
	old_opt_id: number,
	new_opt_id: number,
	owner: string,      // address
	client: string,     // address
	amt: string,
	ret_perc: string,
	price: string,
	ref_price: string,
	delta: string,
	min_price: string,
	borrow: string,
	status: number,     // 0 - crated, 1 - accepted, 2 - pushed, 3 - canceled
	old_option: iOptionResponseOrderless,
	new_option: iOptionResponseOrderless,
}

export const zeroBN = new BN('0');

export type tAddress = `0x${string}`;